import React, { useState } from 'react';
import { Download } from 'lucide-react';

const RuedaVidaTest = () => {
  const [activeTab, setActiveTab] = useState('instrucciones');
  const [scores, setScores] = useState({});

  const areas = {
    personal: {
      nombre: 'ÁREA PERSONAL',
      color: '#E57373',
      aspectos: [
        'Soy consecuente',
        'Dedico tiempo al crecimiento personal',
        'Disfruto momentos de tranquilidad',
        'Felicidad',
        'Imagen de mi mismo y autoestima',
        'Tengo vida estimulante (control el estrés)'
      ]
    },
    espiritual: {
      nombre: 'ÁREA ESPIRITUAL',
      color: '#F06292',
      aspectos: [
        'Tengo claras mis metas',
        'Soy congruente en mi vida con mis valores',
        'Actividades para mi crecimiento personal',
        'Trabajo mis puntos débiles',
        'Me preocupo del crecimiento de los míos',
        'Soy responsable con el medio ambiente'
      ]
    },
    familiar: {
      nombre: 'ÁREA FAMILIAR',
      color: '#9575CD',
      aspectos: [
        'Tiempo para la familia',
        'Relación con la pareja',
        'Relación con padres',
        'Relación con hijos',
        'Relación con otros miembros de la familia',
        'Dar prioridad a la familia'
      ]
    },
    fisica: {
      nombre: 'ÁREA FÍSICA',
      color: '#64B5F6',
      aspectos: [
        'Apariencia',
        'Nivel de energía',
        'Peso',
        'Dieta equilibrada',
        'Resistencia',
        'Estado de Salud en general'
      ]
    },
    economica: {
      nombre: 'ÁREA ECONÓMICA',
      color: '#4DD0E1',
      aspectos: [
        'Ganancias',
        'Presupuesto personal',
        'Control de compras',
        'Control de gastos',
        'Inversiones adecuadas',
        'Balance final mes'
      ]
    },
    social: {
      nombre: 'ÁREA SOCIAL / AFECTIVO',
      color: '#81C784',
      aspectos: [
        'Amistades: Calidad',
        'Cuido a mis amistades',
        'Participación comunidad y trabajos sociales',
        'Disfruto de compañía estimulante',
        'Practico el ocio interesante con amigos',
        'Satisfacción en general con mi vida social'
      ]
    },
    profesional: {
      nombre: 'ÁREA PROFESIONAL',
      color: '#FFD54F',
      aspectos: [
        'Posibilidad desarrollo',
        'Trabajo estimulante',
        'Trabajo en el lugar deseado',
        'Ambiente de trabajo agradable',
        'Se valoran mis capacidades',
        'Amplio conocimientos y me reciclo'
      ]
    },
    ocio: {
      nombre: 'ÁREA ENTRETENIMIENTO',
      color: '#FFB74D',
      aspectos: [
        'Dedico tiempo para el ocio y entretenimiento',
        'Hago Deporte de forma continua',
        'Lectura / Cine / Teatro / Cultura',
        'Elimino los asuntos de trabajo en el ocio',
        'Me divierto y participo en el ocio',
        'Disfruto del tiempo libre en casa y mi soledad'
      ]
    }
  };

  const handleScoreChange = (area, aspecto, value) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.min(Math.max(numValue, 0), 100);
    
    setScores(prev => ({
      ...prev,
      [area]: {
        ...prev[area],
        [aspecto]: clampedValue
      }
    }));
  };

  const calculateAverage = (area) => {
    const areaScores = scores[area];
    if (!areaScores) return 0;
    
    const values = Object.values(areaScores);
    if (values.length === 0) return 0;
    
    const sum = values.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / values.length);
  };

  const getAllAverages = () => {
    const averages = {};
    Object.keys(areas).forEach(areaKey => {
      averages[areaKey] = calculateAverage(areaKey);
    });
    return averages;
  };

  const generatePDF = () => {
    window.print();
  };

  const renderWheel = () => {
    const averages = getAllAverages();
    const size = window.innerWidth < 640 ? 300 : 400;
    const center = size / 2;
    const maxRadius = size / 2 - 20;
    const rings = 4;
    
    const areaKeys = Object.keys(areas);
    const anglePerArea = (2 * Math.PI) / areaKeys.length;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto w-full max-w-md">
        {/* Círculos concéntricos */}
        {[...Array(rings)].map((_, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={(maxRadius / rings) * (i + 1)}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="1"
          />
        ))}

        {/* Líneas divisorias */}
        {areaKeys.map((_, index) => {
          const angle = index * anglePerArea - Math.PI / 2;
          const x2 = center + maxRadius * Math.cos(angle);
          const y2 = center + maxRadius * Math.sin(angle);
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={x2}
              y2={y2}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
          );
        })}

        {/* Segmentos de colores */}
        {areaKeys.map((areaKey, index) => {
          const area = areas[areaKey];
          const average = averages[areaKey];
          const percentage = average / 100;
          const radius = maxRadius * percentage;
          
          const startAngle = index * anglePerArea - Math.PI / 2;
          const endAngle = (index + 1) * anglePerArea - Math.PI / 2;
          
          const x1 = center + radius * Math.cos(startAngle);
          const y1 = center + radius * Math.sin(startAngle);
          const x2 = center + radius * Math.cos(endAngle);
          const y2 = center + radius * Math.sin(endAngle);
          
          const largeArcFlag = anglePerArea > Math.PI ? 1 : 0;
          
          const pathData = [
            `M ${center} ${center}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          return (
            <path
              key={areaKey}
              d={pathData}
              fill={area.color}
              opacity="0.7"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}

        {/* Etiquetas */}
        {areaKeys.map((areaKey, index) => {
          const area = areas[areaKey];
          const angle = (index + 0.5) * anglePerArea - Math.PI / 2;
          const labelRadius = window.innerWidth < 640 ? maxRadius - 10 : maxRadius + 10;
          const x = center + labelRadius * Math.cos(angle);
          const y = center + labelRadius * Math.sin(angle);
          
          return (
            <text
              key={`label-${areaKey}`}
              x={x}
              y={y}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#333"
            >
              {area.nombre.split(' ')[1] || area.nombre}
            </text>
          );
        })}
      </svg>
    );
  };

  const renderInstrucciones = () => (
    <div className="max-w-4xl mx-auto p-4 md:p-8 bg-white rounded-lg shadow-sm">
      <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">
        Test de la Rueda de la Vida
      </h1>
      
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 md:p-6 mb-8">
        <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
          INSTRUCCIONES PARA REALIZAR TEST
        </h2>
        <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
          En la presente hoja encontrarás cada una de las 8 áreas que conforman la rueda de la vida.
        </p>
        <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
          Cada área contiene unos aspectos que evaluarán como te encuentras. Para cada aspecto tienes que darle una puntuación de 1 a 100, donde 1 es el resultado más pobre y 100 es el resultado más excelente.
        </p>
        <p className="text-sm md:text-base text-gray-700 leading-relaxed">
          Automáticamente, se obtendrá la puntuación promedio final en cada área que graficará la rueda de la vida que se encuentra en la siguiente sección.
        </p>
      </div>

      <div className="text-center">
        <button
          onClick={() => setActiveTab('personal')}
          className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Comenzar Test
        </button>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 md:p-6 mt-12">
        <p className="text-sm md:text-base text-gray-700 leading-relaxed">
          Lo que se busca es que esta herramienta sirva como GUIA DE EVALUACION PERSONAL:<br />
          <span className="font-bold">LO QUE NO SE MIDE, NO SE PUEDE EVALUAR NI MEJORAR.</span>
        </p>
      </div>
    </div>
  );

  const renderAreaForm = (areaKey) => {
    const area = areas[areaKey];
    const average = calculateAverage(areaKey);

    return (
      <div className="max-w-3xl mx-auto p-4 md:p-8 bg-white rounded-lg shadow-sm">
        <div 
          className="text-center p-4 rounded-lg mb-6"
          style={{ backgroundColor: area.color + '20', borderLeft: `4px solid ${area.color}` }}
        >
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">{area.nombre}</h2>
          <p className="text-xs md:text-sm text-gray-600 mt-2">
            Califica cada aspecto del 1 al 100 (1 = Pobre, 100 = Excelente)
          </p>
        </div>

        <div className="space-y-4">
          {area.aspectos.map((aspecto, index) => (
            <div key={index} className="flex items-center gap-4">
              <label className="flex-1 text-sm md:text-base text-gray-700">{aspecto}</label>
              <input
                type="number"
                min="1"
                max="100"
                value={scores[areaKey]?.[aspecto] || ''}
                onChange={(e) => handleScoreChange(areaKey, aspecto, e.target.value)}
                className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
              />
            </div>
          ))}
        </div>

        <div 
          className="mt-6 p-4 rounded-lg text-center"
          style={{ backgroundColor: area.color + '30' }}
        >
          <p className="text-lg font-semibold text-gray-800">
            Puntuación Promedio: <span className="text-2xl">{average}</span>
          </p>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              const nextTab = getNextTab(areaKey);
              if (nextTab) setActiveTab(nextTab);
            }}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Siguiente
          </button>
        </div>
      </div>
    );
  };

  const renderVisualizacion = () => {
    const averages = getAllAverages();

    return (
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Tu Rueda de la Vida
        </h1>

        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            INSTRUCCIONES PARA INTERPRETAR TU RUEDA DE LA VIDA
          </h2>
          <div className="text-gray-700 leading-relaxed space-y-3 text-sm md:text-base text-left">
            <ul className='list-disc'>
            <li>
              Cuanto más grande y perfecto sea el círculo que obtienes de tus puntuaciones, más satisfactoria percibes tu vida en todas las áreas.
            </li>
            <li>
              Si el dibujo que obtienes es un pequeño círculo significa que tu vida está equilibrada en todas las áreas... pero en insatisfacción general.
            </li>
            
                <li>Si el círculo presenta picos muy pronunciados, como ocurre en la mayoría de los casos, significa que hay áreas de tu vida a las que debes prestar más atención.</li>
                <li>Si tu dibujo es un gran círculo y con pocos picos estas satisfecho y en equilibrio.</li>
                <li>Las puntuaciones más cercanas al punto central (más bajas) te indican las áreas en las que te sientes más insatisfecho. Por tanto son las primeras que debes trabajar para ir directo al éxito.</li>
                <li>La puntuaciones más lejanas del punto central (más altas) indican satisfacción con el area señalada.</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-2 mb-8">
          {renderWheel()}
          
          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm">
              Cada segmento representa un área de tu vida. El tamaño del segmento indica tu nivel de satisfacción en esa área.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Las áreas más pequeñas son oportunidades de crecimiento y mejora.
            </p>
          </div>
        </div>

        <div className="print-page-break"></div>

        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Resumen de Puntuaciones
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm md:text-base">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left p-2 md:p-3 font-semibold text-gray-700">Área de la Vida</th>
                  <th className="text-center p-2 md:p-3 font-semibold text-gray-700">Puntuación</th>
                  <th className="text-center p-2 md:p-3 font-semibold text-gray-700">Estado</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(areas).map(([key, area]) => {
                  const avg = averages[key];
                  let estado = '';
                  if (avg >= 80) estado = 'Excelente';
                  else if (avg >= 60) estado = 'Bueno';
                  else if (avg >= 40) estado = 'Regular';
                  else if (avg >= 20) estado = 'Necesita atención';
                  else estado = 'Crítico';

                  return (
                    <tr key={key} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-2 md:p-3">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div 
                            className="w-3 h-3 md:w-4 md:h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: area.color }}
                          />
                          <span className="font-medium text-gray-700 text-xs md:text-base">{area.nombre}</span>
                        </div>
                      </td>
                      <td className="text-center p-2 md:p-3">
                        <span className="text-base md:text-lg font-semibold text-gray-800">{avg}</span>
                      </td>
                      <td className="text-center p-2 md:p-3 text-gray-600 text-xs md:text-base">{estado}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 md:p-6 mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
            Siguientes Pasos Recomendados
          </h2>
          <p className="text-gray-700 font-semibold mb-2 text-sm md:text-base"></p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2 text-sm md:text-base">
            <li>Imprime los resultados de esta evaluación para una referencia futura</li>
            <li>Visualiza cómo sería tu vida si estuviera equilibrada</li>
            <li>Haz una lista de las cosas que tienes que mejorar</li>
            <li>Haz una lista de las cosas que tienes que eliminar de tu vida YA</li>
            <li>Ponte una meta en cada una de las pareas que has de mejorar</li>
            <li>Crea un Plan de Acción para cada una de las metas y ponlas en marcha</li>
            <li>Vuelve a realizar este test dentro de 1 mes para comprobar avances</li>
          </ul>
        </div>

        <div className="text-center">
          <button
            onClick={generatePDF}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center gap-2 print:hidden"
          >
            <Download size={20} />
            Guardar / Imprimir Resultados
          </button>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'instrucciones', label: 'Inicio', shortLabel: 'Inicio' },
    { id: 'personal', label: 'Personal', shortLabel: 'Personal' },
    { id: 'espiritual', label: 'Espiritual', shortLabel: 'Espiritual' },
    { id: 'familiar', label: 'Familiar', shortLabel: 'Famila' },
    { id: 'fisica', label: 'Física', shortLabel: 'Física' },
    { id: 'economica', label: 'Económica', shortLabel: 'Económica' },
    { id: 'social', label: 'Social', shortLabel: 'Social' },
    { id: 'profesional', label: 'Profesional', shortLabel: 'Profesional' },
    { id: 'ocio', label: 'Entretenimiento', shortLabel: 'Entret.' },
    { id: 'visualizacion', label: 'Total', shortLabel: 'Total' }
  ];

  const getNextTab = (currentTab) => {
    const currentIndex = tabs.findIndex(tab => tab.id === currentTab);
    if (currentIndex < tabs.length - 1) {
      return tabs[currentIndex + 1].id;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto print:hidden">
          <div className="flex flex-wrap gap-1 md:gap-2 p-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 md:px-4 py-2 rounded-md font-medium transition-colors text-xs md:text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'instrucciones' && renderInstrucciones()}
        {activeTab === 'personal' && renderAreaForm('personal')}
        {activeTab === 'espiritual' && renderAreaForm('espiritual')}
        {activeTab === 'familiar' && renderAreaForm('familiar')}
        {activeTab === 'fisica' && renderAreaForm('fisica')}
        {activeTab === 'economica' && renderAreaForm('economica')}
        {activeTab === 'social' && renderAreaForm('social')}
        {activeTab === 'profesional' && renderAreaForm('profesional')}
        {activeTab === 'ocio' && renderAreaForm('ocio')}
        {activeTab === 'visualizacion' && renderVisualizacion()}
      </div>
    </div>
  );
};

export default RuedaVidaTest;